import { api } from "@/lib/api/client";
import { commitOptions } from "./commitSeams";
import { withVersion } from "./conflict";

export type CoverageState = "present" | "explicitly_omitted" | "never_sourced";
export type CoverageFacet = "roster" | "credits" | "outcomes" | "attendance" | "materials";
export type ProvenanceState = "native" | "imported";

export interface HistoryCoverage {
  roster: CoverageState;
  credits: CoverageState;
  outcomes: CoverageState;
  attendance: CoverageState;
  materials: CoverageState;
}

export interface HistoryProvenance {
  state: ProvenanceState;
  source_name: string | null;
  first_batch_id: string | null;
  imported_at: string | null;
  correction_state: string | null;
  correction_reason: string | null;
  correction_at: string | null;
}

export interface HistoryContinuation {
  lineage_id: string;
  course_id: string;
  code: string;
  title: string;
  term_name: string | null;
  term_status: string | null;
  recorded_by: string;
  recorded_at: string;
}

export interface CourseHistoryRow {
  id: string;
  term_id: string;
  term_name: string;
  term_start_date: string | null;
  term_end_date: string | null;
  code: string;
  title: string;
  crn: string | null;
  program_id: string | null;
  program_name: string | null;
  faculty_id: string | null;
  faculty_profile_id: string | null;
  faculty_label: string;
  instructors: HistoricalInstructor[];
  student_count: number;
  coverage: HistoryCoverage;
  provenance: HistoryProvenance;
  continuation: HistoryContinuation | null;
}

export interface HistoricalInstructor {
  faculty_profile_id: string;
  display_name: string;
  assignment_kind: "primary" | "co_instructor";
}

export interface HistoryFacetOption {
  value: string | null;
  label: string;
  count: number;
  end_date?: string | null;
}

export interface CourseHistoryPage {
  items: CourseHistoryRow[];
  total: number;
  page: number;
  page_size: number;
  summary: {
    closed_sections: number;
    imported_sections: number;
    with_continuation: number;
    filtered_sections: number;
  };
  facets: {
    terms: HistoryFacetOption[];
    programs: HistoryFacetOption[];
    faculty: HistoryFacetOption[];
  };
}

export interface CourseHistoryFilters {
  q?: string;
  term_id?: string;
  program_id?: string;
  faculty_id?: string;
  roster?: CoverageState;
  credits?: CoverageState;
  outcomes?: CoverageState;
  attendance?: CoverageState;
  materials?: CoverageState;
  provenance?: ProvenanceState;
  has_continuation?: boolean;
  page?: number;
  page_size?: number;
}

export interface HistoricalRosterRow {
  enrollment_id: string;
  student_id: string;
  sid: string;
  legal_name: string;
  preferred_name: string | null;
  honorific: string | null;
  status: string;
  participation_kind: "credit" | "audit";
  enrolled_at: string;
  withdrawn_at: string | null;
}

export interface HistoricalCertification {
  id: string;
  enrollment_id: string;
  student_id: string;
  sid: string;
  legal_name: string;
  event_no: number;
  kind: "certification" | "correction";
  certified_letter: string;
  certified_score: number | null;
  prior_letter: string | null;
  prior_score: number | null;
  reason: string | null;
  downstream_impact: string | null;
  evidence_ref: string;
  actor_user_id: string;
  created_at: string;
}

export interface HistoricalMaterial {
  id: string;
  week_no: number | null;
  title: string;
  file_url: string | null;
  file_size: number | null;
  material_type: string | null;
  google_drive_mime_type: string | null;
  sort_order: number;
}

export interface LineageStep {
  depth: number;
  lineage_id: string;
  predecessor_course_id: string;
  predecessor_code: string;
  predecessor_title: string;
  predecessor_term_name: string | null;
  successor_course_id: string;
  successor_code: string;
  successor_title: string;
  successor_term_name: string | null;
  successor_term_status: string | null;
  recorded_by: string;
  recorded_at: string;
}

export interface LineageRemoval {
  id: string;
  successor_course_id: string;
  successor_code: string;
  successor_title: string;
  recorded_by: string;
  recorded_at: string;
  removed_by: string;
  removed_at: string;
}

export interface ImportReceiptEvidence {
  id: string;
  batch_id: string;
  source_name?: string;
  mode?: "apply" | "dry_run";
  import_key: string;
  entity_kind: string;
  source_ref: Record<string, unknown>;
  resolved_id: string;
  action: string;
  reason: string | null;
  corrects_receipt_id: string | null;
  prior_value: Record<string, unknown> | null;
  created_at: string;
}

/**
 * One field whose repeated import receipts disagree.
 *
 * The projection never picks between them: the field itself is `null` on the
 * item and every variant is named here with the receipts and batches that
 * carried it. A conflict is a fact for the registrar to resolve through a
 * governed correction, not something the read model is entitled to settle.
 */
export interface HistoricalEvidenceConflict {
  field: string;
  values: Array<{
    value: unknown;
    receipt_ids: string[];
    batch_ids: string[];
  }>;
}

export interface HistoricalEvidenceProvenance {
  receipt_ids: string[];
  batch_ids: string[];
  conflicts: HistoricalEvidenceConflict[];
}

export interface HistoricalSourceCourse extends HistoricalEvidenceProvenance {
  provider: string | null;
  source_course_id: string | null;
  source_name: string | null;
  source_section: string | null;
  course_state: string | null;
  captured_at: string | null;
  source_creation_time: string | null;
  source_update_time: string | null;
  gradebook_settings: {
    calculation_type: string | null;
    display_setting: string | null;
  } | null;
}

export interface HistoricalCourseworkEvidence extends HistoricalEvidenceProvenance {
  source_coursework_id: string;
  title: string | null;
  state: string | null;
  work_type: string | null;
  max_points: number | null;
  creation_time: string | null;
  update_time: string | null;
  /** The source's own calendar fields. No timezone is inferred for either. */
  due_date: { year: number; month: number; day: number } | null;
  due_time: { hours?: number; minutes?: number } | null;
}

export interface HistoricalSubmissionEvidence extends HistoricalEvidenceProvenance {
  enrollment_id: string;
  student_id: string;
  sid: string;
  legal_name: string;
  preferred_name: string | null;
  source_coursework_id: string;
  source_submission_id: string | null;
  coursework_title: string | null;
  state: string | null;
  assigned_grade: number | null;
  draft_grade: number | null;
  late: boolean | null;
  creation_time: string | null;
  update_time: string | null;
}

export interface CourseHistoryDetail {
  continuation_version: string;
  course: {
    id: string;
    code: string;
    title: string;
    title_ar: string | null;
    crn: string | null;
    credits: number | null;
    status: string;
    program_id: string | null;
    program_name: string | null;
    term_id: string;
    term_name: string;
    term_start_date: string | null;
    term_end_date: string | null;
    primary_instructor_user_id: string | null;
    faculty_profile_id: string | null;
    faculty_honorific: string | null;
    faculty_email: string | null;
    faculty_title_role: string | null;
    instructors: HistoricalInstructor[];
  };
  roster: HistoricalRosterRow[];
  certifications: HistoricalCertification[];
  attendance: {
    sessions: number;
    held_sessions: number;
    marks: number;
    present: number;
    late: number;
    absent: number;
    excused: number;
  };
  materials: HistoricalMaterial[];
  /**
   * Historical source evidence, projected read-only from applied import
   * receipts. A Classroom grade here is evidence about a source. It is never a
   * working grade, a certified outcome, or a GPA input.
   */
  source_course: HistoricalSourceCourse | null;
  coursework_evidence: HistoricalCourseworkEvidence[];
  submission_evidence: HistoricalSubmissionEvidence[];
  provenance: {
    state: ProvenanceState;
    identity?: {
      import_key: string;
      first_batch_id: string;
      source_name: string;
      source_digest: string;
      outcome: string | null;
      observations: Array<{ kind: string; detail: string }>;
      actor_user_id: string;
      started_at: string;
      finished_at: string | null;
    };
    identities?: NonNullable<CourseHistoryDetail["provenance"]["identity"]>[];
    receipts: ImportReceiptEvidence[];
    omissions: Array<{
      facet: CoverageFacet;
      state: "explicitly_omitted";
      evidence: string;
      batch_id: string;
    }>;
  };
  lineage_chain: LineageStep[];
  lineage_removals: LineageRemoval[];
}

export interface SuccessorCandidate {
  id: string;
  code: string;
  title: string;
  crn: string | null;
  term_name: string;
  term_status: "active" | "upcoming";
}

function query(filters: CourseHistoryFilters): string {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== "") params.set(key, String(value));
  });
  const value = params.toString();
  return value ? `?${value}` : "";
}

export const courseHistoryApi = {
  list: (filters: CourseHistoryFilters = {}) =>
    api.get<CourseHistoryPage>(`/course-history${query(filters)}`),
  // ADR 0006: Pages and Cloud Run share no transaction, so a frontend release
  // can serve against the previous backend for a window. A detail payload from
  // before issue #350 carries none of the three evidence keys, and the record
  // sheet reads their `.length` while rendering - so the whole sheet would
  // throw rather than degrade. The absent keys become their empty shape here,
  // once, instead of every reader guarding.
  get: async (courseId: string): Promise<CourseHistoryDetail> => {
    const detail = await api.get<CourseHistoryDetail>(`/course-history/${courseId}`);
    return {
      ...detail,
      source_course: detail.source_course ?? null,
      coursework_evidence: detail.coursework_evidence ?? [],
      submission_evidence: detail.submission_evidence ?? [],
    };
  },
  successors: (courseId: string, q?: string) =>
    api.get<SuccessorCandidate[]>(`/course-history/${courseId}/successors${query({ q })}`),
  recordLineage: (courseId: string, successor_course_id: string, expected_updated_at: string, key: string) =>
    api.post<ContinuationResult>(`/course-history/${courseId}/lineage`, withVersion({ successor_course_id }, expected_updated_at), commitOptions(key)),
  removeLineage: (courseId: string, expected_updated_at: string, key: string) =>
    api.delete<ContinuationResult>(`/course-history/${courseId}/lineage`, withVersion({}, expected_updated_at), commitOptions(key)),
};

export interface ContinuationResult { state: "confirmed"; course_id: string; lineage_id: string; version: string; operation: "recorded" | "removed" }
